import { buildDeliveryNoteUnitOptions } from "./deliveryNoteUnits";
import { filterOcrAiSupplierItems, findOcrAiItemMatch } from "./ocrAiItemMatch";

const text = (value) => typeof value === "string" ? value.trim() : "";
const normalized = (value) => text(value).normalize("NFKC").toLocaleLowerCase().replace(/\s+/g, " ");
const numeric = (value) => value === null || value === undefined || value === "" ? null : Number(String(value).replace(",", "."));
const sameNumber = (left, right) => numeric(left) === numeric(right);
const retained = (value, previous = "") => value === null || value === undefined ? previous : value;
const itemFor = (line, items) => items.find((item) => String(item.id) === String(line.suggested_item_id || line.suggested_item?.id || ""));

const UNIT_ALIASES = {
  kg: "kg", k: "kg", kilo: "kg", kilos: "kg", kilogram: "kg", kilograms: "kg", kilogramo: "kg", kilogramos: "kg",
  g: "g", gr: "g", gram: "g", grams: "g", gramo: "g", gramos: "g",
  l: "l", lt: "l", liter: "l", liters: "l", litre: "l", litres: "l", litro: "l", litros: "l",
  ml: "ml", milliliter: "ml", millilitre: "ml", mililitro: "ml",
  ud: "unit", uds: "unit", unit: "unit", units: "unit", unidad: "unit", unidades: "unit", unitat: "unit", unitats: "unit",
  box: "box", caja: "box", cajas: "box", caixa: "box", caixes: "box",
  bundle: "bundle", mj: "bundle", manojo: "bundle", manojos: "bundle", manat: "bundle",
  tray: "tray", bj: "tray", bandeja: "tray", bandejas: "tray", safata: "tray",
};
const unitKey = (value) => {
  const key = normalized(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\.$/, "");
  return UNIT_ALIASES[key] || key;
};

// A sole configured unit is unambiguous. With alternatives, require a printed match;
// never infer a pack factor or convert the document's quantity/price here.
export function resolveOcrAiUnitOption(item, candidate) {
  const options = buildDeliveryNoteUnitOptions(item);
  if (options.length === 1) return options[0];
  const label = unitKey(candidate.unit_label);
  if (!label) return null;
  const matches = options.filter((option) => unitKey(option.label) === label);
  if (matches.length !== 1) return null;
  return !candidate.unit_level || matches[0].level === candidate.unit_level ? matches[0] : null;
}

// A supplier change may need a new catalogue request before the applied rows can link.
export function completePendingOcrAiItemMatch(line, items, supplierId) {
  if (!supplierId || String(line._aiMatchSupplierId) !== String(supplierId) ||
      line._itemSelectionConfirmed || line._ocrUnitSelectionManual) return line;
  // A restored draft may have been reviewed before this supplier alias was learned.
  if (!line._aiPendingItemMatch && (!line._aiNeedsItemSelection || line.suggested_item_id)) return line;
  const match = findOcrAiItemMatch(line, items, { supplierId });
  if (!match && !line._aiPendingItemMatch) return line;
  const option = match && resolveOcrAiUnitOption(match.item, {
    unit_label: line._aiProposedUnitLabel, unit_level: line._aiProposedUnitLevel,
  });
  return {
    ...line, suggested_item_id: match ? String(match.item.id) : "", suggested_item: match?.suggestion || null,
    unit_level: option?.level || "", _aiNeedsItemSelection: !match, _aiNeedsUnitSelection: !option,
    _aiPendingItemMatch: false, _itemSelectionConfirmed: false,
    po_receipts: [], is_free_stock: false, free_stock_reason: "",
  };
}

// Match formatting and explicit legal suffixes, never approximate company names.
const supplierKey = (value) => normalized(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ").trim()
  .replace(/(?:^|\s)(?:s\s*l\s*u|s\s*a\s*u|s\s*l|s\s*a|sociedad limitada(?: unipersonal)?|sociedad anonima(?: unipersonal)?)$/, "")
  .replace(/\s+/g, "");

function resolveProposalSupplier(name, review, suppliers) {
  const current = suppliers.find((candidate) => String(candidate.id) === String(review.supplierId));
  if (current && normalized(name) === normalized(review.supplierName)) return current;
  const key = supplierKey(name);
  const matches = key ? suppliers.filter((candidate) => supplierKey(candidate.name) === key) : [];
  return matches.length === 1 ? matches[0] : null;
}

export function buildOcrAiCurrentReview(review, items) {
  return {
    header: { supplier: review.supplierName || "", number: review.deliveryNumber || "", date: review.deliveryDate || null },
    lines: review.lines.map((line) => {
      const options = buildDeliveryNoteUnitOptions(itemFor(line, items) || {});
      return {
        description: line.description || "", supplier_code: line.supplier_code || "",
        quantity: numeric(line.quantity), unit_price: numeric(line.unit_price),
        expiration_date: line.expiration_date || null,
        unit_level: line.unit_level || null,
        unit_label: options.find((option) => option.level === line.unit_level)?.label || null,
        unit_options: options.map((option) => ({ value: option.level, label: option.label, factor: option.factor })),
      };
    }),
    expenses: review.expenses.map((expense) => ({ description: expense.description || "", amount: numeric(expense.amount) })),
  };
}

// Include local assignments and confirmations as well as the values sent to AI.
export function ocrAiDraftSignature(review, items) {
  return JSON.stringify([review, buildOcrAiCurrentReview(review, items)]);
}

export function validateOcrAiProposal(proposal, sourceLineCount) {
  if (!proposal || !proposal.header || !Array.isArray(proposal.lines) || !Array.isArray(proposal.expenses) || !Array.isArray(proposal.warnings)) {
    throw new Error("Invalid AI review");
  }
  const sourceIndexes = new Set();
  for (const line of proposal.lines) {
    const index = line?.source_line_index;
    if (!line || (index !== null && (!Number.isInteger(index) || index < 0 || index >= sourceLineCount || sourceIndexes.has(index)))) {
      throw new Error("Invalid AI line correspondence");
    }
    if (index !== null) sourceIndexes.add(index);
  }
  return proposal;
}

export function applyOcrAiProposal(review, proposal, {
  items = [], suppliers = [], itemsSupplierId = review.supplierId, itemsLoaded = true,
} = {}) {
  validateOcrAiProposal(proposal, review.lines.length);
  const supplierName = review.supplierLocked ? review.supplierName : retained(proposal.header.supplier, review.supplierName);
  const supplier = review.supplierLocked
    ? suppliers.find((candidate) => String(candidate.id) === String(review.supplierId))
    : resolveProposalSupplier(supplierName, review, suppliers);
  const supplierId = supplier ? String(supplier.id) : "";
  const sameSupplier = supplierId === String(review.supplierId || "");
  const catalogueReady = Boolean(supplierId && itemsLoaded && String(itemsSupplierId) === supplierId);
  const catalogue = catalogueReady ? filterOcrAiSupplierItems(items, supplierId) : [];
  const lines = proposal.lines.map((candidate) => {
    const previous = candidate.source_line_index === null ? null : review.lines[candidate.source_line_index];
    const description = retained(candidate.description, previous?.description || "");
    const supplierCode = retained(candidate.supplier_code, previous?.supplier_code || "");
    const identityUnchanged = Boolean(previous && sameSupplier &&
      normalized(description) === normalized(previous.description) && normalized(supplierCode) === normalized(previous.supplier_code));
    const retainedItem = identityUnchanged ? itemFor(previous, catalogue) : null;
    const match = !retainedItem && catalogueReady
      ? findOcrAiItemMatch({ description, supplier_code: supplierCode }, catalogue, { supplierId }) : null;
    const item = retainedItem || match?.item;
    const option = item ? resolveOcrAiUnitOption(item, candidate) : null;
    const quantity = retained(candidate.quantity, previous?.quantity ?? "");
    const price = retained(candidate.unit_price, previous?.unit_price ?? "");
    const unitLevel = option?.level || "";
    const unchanged = Boolean(identityUnchanged && retainedItem && unitLevel && previous.unit_level === unitLevel &&
      sameNumber(quantity, previous.quantity) && sameNumber(price, previous.unit_price));
    return {
      ...(previous || {}), description, supplier_code: supplierCode,
      ocr_description: previous?.ocr_description || previous?.description || description,
      quantity, unit_price: price, unit_level: unitLevel,
      expiration_date: Object.prototype.hasOwnProperty.call(candidate, "expiration_date") ? candidate.expiration_date : previous?.expiration_date || null,
      suggested_item_id: item ? String(item.id) : "",
      suggested_item: match?.suggestion || (retainedItem ? previous.suggested_item || { id: item.id, name: item.name, code: item.code } : null),
      _itemSelectionConfirmed: Boolean(retainedItem && previous?._itemSelectionConfirmed),
      is_free_stock: unchanged ? Boolean(previous.is_free_stock) : false,
      free_stock_reason: unchanged ? previous.free_stock_reason || "" : "",
      po_receipts: unchanged ? previous.po_receipts || [] : [],
      _aiNeedsItemSelection: !item,
      _aiNeedsUnitSelection: !unitLevel,
      _aiPendingItemMatch: Boolean(supplierId && !catalogueReady),
      _aiMatchSupplierId: supplierId,
      _aiProposedUnitLabel: candidate.unit_label,
      _aiProposedUnitLevel: candidate.unit_level,
    };
  });
  return {
    ...review,
    supplierId, supplierName: supplier?.name || supplierName,
    supplierOcrName: review.supplierLocked ? null : text(proposal.header.supplier) || review.supplierOcrName || null,
    supplierDraftName: supplier ? null : supplierName,
    aiTotals: proposal.totals || null,
    aiWarnings: proposal.warnings,
    aiDateConfirmed: Boolean(proposal.header.date),
    deliveryNumber: retained(proposal.header.number, review.deliveryNumber),
    deliveryDate: retained(proposal.header.date, review.deliveryDate),
    lines,
    expenses: proposal.expenses.map((expense, index) => {
      const previous = review.expenses[index];
      const unchanged = previous && normalized(previous.description) === normalized(expense.description) && sameNumber(previous.amount, expense.amount);
      return { description: expense.description || "", amount: expense.amount ?? "", distribute_cost: unchanged ? Boolean(previous.distribute_cost) : false,
        ...(unchanged && previous.allocation ? { allocation: previous.allocation } : {}) };
    }),
  };
}

export function ocrAiComparisonRows(current, proposal) {
  const rows = [];
  const add = (section, field, before, after, kind = "changed", reason = "") => {
    const unknown = after === undefined || (after === null && field !== "Expiration date");
    if (kind === "changed" && !unknown && String(before ?? "") === String(after ?? "")) return;
    rows.push({ section, field, before, after, kind, unknown, reason });
  };
  for (const [key, label] of [["supplier", "Supplier"], ["number", "Number"], ["date", "Date"]]) add("Header", label, current.header[key], proposal.header[key]);
  const matched = new Set();
  const fields = [["description", "Description"], ["supplier_code", "Supplier code"], ["quantity", "Quantity"], ["unit_price", "Unit price"], ["expiration_date", "Expiration date"], ["unit_label", "Unit on note"], ["unit_level", "Unit level"], ["total", "Line total"]];
  proposal.lines.forEach((line, index) => {
    const source = line.source_line_index;
    const previous = source === null ? null : current.lines[source];
    if (source !== null) matched.add(source);
    const before = previous ? { ...previous, total: previous.quantity !== null && previous.unit_price !== null ? previous.quantity * previous.unit_price : null } : {};
    fields.forEach(([key, label]) => {
      if (key === "expiration_date" && !Object.prototype.hasOwnProperty.call(line, key)) return;
      add({ line: index + 1, source: source === null ? null : source + 1 }, label, before[key], line[key], previous ? "changed" : "added", line.reason);
    });
  });
  current.lines.forEach((line, index) => {
    if (matched.has(index)) return;
    const before = { ...line, total: line.quantity !== null && line.unit_price !== null ? line.quantity * line.unit_price : null };
    fields.forEach(([key, label]) => add({ line: index + 1 }, label, before[key], null, "removed"));
  });
  const expenseCount = Math.max(current.expenses.length, proposal.expenses.length);
  for (let index = 0; index < expenseCount; index += 1) {
    const before = current.expenses[index], after = proposal.expenses[index];
    for (const [key, label] of [["description", "Description"], ["amount", "Amount"]]) {
      add({ expense: index + 1 }, label, before?.[key], after?.[key], !after ? "removed" : !before ? "added" : "changed", after?.reason);
    }
  }
  return rows;
}
